/**
 * Add Member Modal Component
 * Modal for adding members to a channel
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as dmService from '@/services/dm.service';
import * as channelsService from '@/services/channels.service';
import type { Channel } from '@/types/api.types';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface AddMemberModalProps {
  channel: Channel;
  onClose: () => void;
  onMembersAdded?: () => void;
}

export const AddMemberModal = ({
  channel,
  onClose,
  onMembersAdded,
}: AddMemberModalProps) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  // Fetch all users
  const { data: allUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: dmService.listUsers,
  });

  // Fetch current channel members
  const { data: currentMembers, isLoading: isLoadingMembers } = useQuery({
    queryKey: ['channel-members', channel.id],
    queryFn: () => channelsService.getChannelMembers(channel.id),
  });

  // Filter out users who are already members
  const availableUsers = useMemo(() => {
    if (!allUsers || !currentMembers) return [];

    const memberUserIds = new Set(currentMembers.map((m) => m.user_id));
    return allUsers.filter((user) => !memberUserIds.has(user.id));
  }, [allUsers, currentMembers]);

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return availableUsers;

    const query = searchQuery.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user.display_name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
    );
  }, [availableUsers, searchQuery]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddMembers = async () => {
    if (selectedUserIds.length === 0) {
      setError('Моля, изберете поне един потребител');
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      // Add each selected user to the channel
      await Promise.all(
        selectedUserIds.map((userId) =>
          channelsService.addChannelMember(channel.id, userId)
        )
      );

      onMembersAdded?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при добавяне на членове');
    } finally {
      setIsAdding(false);
    }
  };

  const isLoading = isLoadingUsers || isLoadingMembers;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            Добави членове към канал
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input"
            placeholder="Търси по име или имейл..."
            autoFocus
          />
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!isLoading && filteredUsers.length === 0 && (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                {searchQuery
                  ? 'Не са намерени потребители'
                  : 'Всички потребители вече са членове на канала'}
              </p>
            </div>
          )}

          {!isLoading && filteredUsers.length > 0 && (
            <div className="space-y-1">
              {filteredUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(user.id)}
                    onChange={() => handleToggleUser(user.id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      {/* Avatar */}
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.display_name}
                          className="w-8 h-8 rounded-full mr-3"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium mr-3">
                          {user.display_name.charAt(0).toUpperCase()}
                        </div>
                      )}

                      {/* User Info */}
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {user.display_name}
                        </div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedUserIds.length > 0 ? (
              <>
                Избрани {selectedUserIds.length}{' '}
                {selectedUserIds.length === 1 ? 'потребител' : 'потребители'}
              </>
            ) : (
              'Няма избрани потребители'
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={isAdding}
            >
              Отказ
            </button>
            <button
              onClick={handleAddMembers}
              className="btn-primary"
              disabled={isAdding || selectedUserIds.length === 0}
            >
              {isAdding ? 'Добавяне...' : 'Добави членове'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
